import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { fetchRecentAlarms } from '../services/api';
import { useLocation } from 'react-router-dom';

const AlarmNotificationContext = createContext(null);

// AudioContext 싱글턴 (첫 클릭 시 초기화)
let sharedAudioCtx = null;
const initAudioContext = () => {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedAudioCtx;
};

// 첫 사용자 인터랙션에서 AudioContext 생성
if (typeof window !== 'undefined') {
  const initOnce = () => {
    initAudioContext();
    document.removeEventListener('click', initOnce);
    document.removeEventListener('keydown', initOnce);
  };
  document.addEventListener('click', initOnce, { once: false });
  document.addEventListener('keydown', initOnce, { once: false });
}

function playAlarmSound() {
  try {
    const ctx = initAudioContext();
    if (ctx.state === 'suspended') ctx.resume();

    [0, 2.2, 4.4].forEach((cycleStart) => {
      [0, 0.4, 0.8].forEach((beep) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.value = 0.3;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + cycleStart + beep);
        osc.stop(ctx.currentTime + cycleStart + beep + 0.15);
      });
    });
  } catch (e) {
    console.error('알람 소리 재생 실패:', e);
  }
}

function formatAlarmTime(raw) {
  if (!raw) return new Date().toLocaleString('ko-KR');
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString('ko-KR');
  } catch {
    return raw;
  }
}

// 현재 경로 기반 필터 컨텍스트 결정
function getFilterContext(pathname, searchParams) {
  // 설비현황판
  if (pathname.includes('/equipment-board')) {
    return { type: 'equipment-board' };
  }
  // 사출기 모니터링 (machine?id=XX) — /machine-data 등은 제외
  if (pathname === '/machine' || pathname.endsWith('/machine')) {
    const id = searchParams.get('id');
    return id ? { type: 'equipment', equipmentId: parseInt(id) } : { type: 'all' };
  }
  // 공장 모니터링 (factory?factoryId=XX)
  if (pathname.includes('/factory') || pathname.includes('/factory-machine-overview') || pathname.includes('/realtime-shot-status')) {
    const fid = searchParams.get('factoryId');
    return fid ? { type: 'factory', factoryId: parseInt(fid) } : { type: 'all' };
  }
  // home, 전사모니터링, 기타 → 전체
  return { type: 'all' };
}

export function AlarmNotificationProvider({ children }) {
  const [alarms, setAlarms] = useState([]);
  const [equipmentBoardIds, setEquipmentBoardIds] = useState([]);
  const knownAlarmIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const location = useLocation();

  const filterContext = getFilterContext(location.pathname, new URLSearchParams(location.search));

  // 알람 폴링 (5초)
  useEffect(() => {
    const poll = async () => {
      try {
        const data = await fetchRecentAlarms(50);
        if (!data?.recentAlarms) return;

        const newAlarms = [];
        for (const alarm of data.recentAlarms) {
          const key = `${alarm.alarmId}_${alarm.code}_${alarm.time}`;
          if (!knownAlarmIdsRef.current.has(key)) {
            knownAlarmIdsRef.current.add(key);
            if (!isFirstLoadRef.current) {
              newAlarms.push({
                id: key,
                alarmId: alarm.alarmId,
                equipmentId: alarm.equipmentId,
                equipmentName: alarm.equipmentName || `설비 #${alarm.equipmentId}`,
                factoryId: alarm.factoryId,
                factoryName: alarm.factoryName || '',
                code: alarm.alarmCode || alarm.code || '',
                message: alarm.message || '',
                severity: alarm.severity || 'WARNING',
                time: formatAlarmTime(alarm.createdAt || alarm.occurredAt || alarm.time),
                timeAgo: alarm.timeAgo || '',
              });
            }
          }
        }
        isFirstLoadRef.current = false;

        if (newAlarms.length > 0) {
          // 필터 적용
          const filtered = filterAlarms(newAlarms, filterContext, equipmentBoardIds);
          if (filtered.length > 0) {
            playAlarmSound();
            setAlarms(prev => [...filtered, ...prev]);
          }
        }
      } catch (e) {
        // 무시
      }
    };

    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [filterContext.type, filterContext.factoryId, filterContext.equipmentId, equipmentBoardIds]);

  // 페이지 변경 시 known 알람은 유지하되 표시된 팝업은 초기화
  useEffect(() => {
    setAlarms([]);
  }, [location.pathname]);

  const removeAlarm = useCallback((id) => {
    setAlarms(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearAllAlarms = useCallback(() => {
    setAlarms([]);
  }, []);

  // 설비현황판에서 선택된 장비 ID 업데이트
  const updateEquipmentBoardIds = useCallback((ids) => {
    setEquipmentBoardIds(ids);
  }, []);

  return (
    <AlarmNotificationContext.Provider value={{
      alarms,
      removeAlarm,
      clearAllAlarms,
      updateEquipmentBoardIds,
      filterContext,
    }}>
      {children}
      {/* 설비현황판에서는 패널 내부에 표시하므로 글로벌 팝업 숨김 */}
      {filterContext.type !== 'equipment-board' && (
        <AlarmPopupStack alarms={alarms} removeAlarm={removeAlarm} filterContext={filterContext} />
      )}
    </AlarmNotificationContext.Provider>
  );
}

function filterAlarms(alarms, filterContext, equipmentBoardIds) {
  switch (filterContext.type) {
    case 'factory':
      return alarms.filter(a => a.factoryId === filterContext.factoryId);
    case 'equipment':
      return alarms.filter(a => a.equipmentId === filterContext.equipmentId);
    case 'equipment-board':
      if (equipmentBoardIds.length === 0) return alarms;
      return alarms.filter(a => equipmentBoardIds.includes(a.equipmentId));
    case 'all':
    default:
      return alarms;
  }
}

function AlarmPopupStack({ alarms, removeAlarm, filterContext }) {
  if (alarms.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      className="alarm-stack"
      style={{
        position: 'fixed',
        top: '70px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxHeight: 'calc(100vh - 90px)',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <style>{`
        .alarm-stack::-webkit-scrollbar { display: none; }
        @keyframes alarmSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes alarmPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {alarms.map((alarm) => (
        <div key={alarm.id} style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          border: `2px solid ${alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B'}`,
          borderRadius: '4px',
          padding: '16px 20px',
          minWidth: '340px',
          maxWidth: '380px',
          boxShadow: `0 8px 32px ${alarm.severity === 'CRITICAL' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
          animation: 'alarmSlideIn 0.3s ease-out',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{
              fontSize: '18px',
              marginRight: '8px',
              animation: 'alarmPulse 1s infinite'
            }}>🚨</span>
            <span style={{
              color: alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>설비 알람 발생</span>
            <button
              onClick={() => removeAlarm(alarm.id)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >✕</button>
          </div>

          <div style={{
            background: 'rgba(239,68,68,0.1)',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '8px'
          }}>
            <div style={{ color: '#ccc', fontSize: '12px', marginBottom: '2px' }}>설비</div>
            <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
              {alarm.equipmentName}{alarm.factoryName ? ` (${alarm.factoryName})` : ''}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <div style={{
              flex: 1,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '11px' }}>알람 코드</div>
              <div style={{ color: '#F59E0B', fontSize: '13px', fontWeight: 'bold' }}>{alarm.code}</div>
            </div>
            <div style={{
              flex: 1,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '11px' }}>심각도</div>
              <div style={{
                color: alarm.severity === 'CRITICAL' ? '#EF4444' : '#F59E0B',
                fontSize: '13px',
                fontWeight: 'bold'
              }}>{alarm.severity}</div>
            </div>
          </div>

          {alarm.message && (
            <div style={{ color: '#aaa', fontSize: '12px' }}>{alarm.message}</div>
          )}
          <div style={{ color: '#666', fontSize: '11px', marginTop: '6px' }}>{alarm.time}</div>
        </div>
      ))}
    </div>,
    document.body
  );
}

export function useAlarmNotification() {
  const context = useContext(AlarmNotificationContext);
  if (!context) {
    throw new Error('useAlarmNotification must be used within AlarmNotificationProvider');
  }
  return context;
}

export default AlarmNotificationContext;
